'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { OnChangeFn, Updater, VisibilityState } from '@tanstack/react-table';
import type { TableColumnDef } from './column';

export type TableColumnVisibility<T extends object> = Record<keyof T, boolean>;
export type TableOnColumnVisibilityChange<T extends object> = (state: TableColumnVisibility<T>) => void;

function getInitialColumnVisibility<TData extends object, TValue>(
  columns: TableColumnDef<TData, TValue>[],
  controlledColumnVisibility: TableColumnVisibility<TData> | undefined,
): VisibilityState {
  return columns.reduce((visibility, column) => {
    visibility[column.id] = controlledColumnVisibility?.[column.id as keyof TData] || true;

    return visibility;
  }, {} as VisibilityState);
}

export function useColumnVisibility<TData extends object, TValue>(
  columns: TableColumnDef<TData, TValue>[],
  controlledColumnVisibility: TableColumnVisibility<TData> | undefined,
  onColumnResize: TableOnColumnVisibilityChange<TData> | undefined,
): [VisibilityState, OnChangeFn<VisibilityState>] {
  const hasUserUpdatedRef = useRef(false);
  const [state, setState] = useState<VisibilityState>(getInitialColumnVisibility(columns, controlledColumnVisibility));
  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = useCallback(
    (updater: Updater<VisibilityState>) => {
      const result = typeof updater === 'function' ? updater(state) : updater;
      setState(result);

      hasUserUpdatedRef.current = true;

      if (onColumnResize) {
        onColumnResize(result as TableColumnVisibility<TData>);
      }

      return result;
    },
    [state, onColumnResize],
  );

  useEffect(() => {
    if (!hasUserUpdatedRef.current) {
      setState(getInitialColumnVisibility(columns, controlledColumnVisibility));
    }
  }, [columns, controlledColumnVisibility]);

  return useMemo(() => [state, handleColumnVisibilityChange], [state, handleColumnVisibilityChange]);
}
